<template>
  <div>
    <b-row class="border-bottom mb-2">
      <b-col>
        <h3 class="mb-0">
          Network
        </h3>
        <small
          class="text-muted"
        >Select the network that device will use to connect to</small>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col>
        <b-form-input
          v-model="search"
          type="search"
          placeholder="Search for a network"
          @input="filterNetworks"
        />
      </b-col>
    </b-row>
    <validation-provider
      v-slot="{ errors }"
      ref="networkValidator"
      :rules="{ network_selected: selectedNetwork }"
    >
      <b-row>
        <b-col
          v-for="network in filteredNetworks"
          :key="network.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          class="mb-4"
        >
          <b-card
            class="h-100 cursor-pointer"
            :class="selectedClass(network)"
            no-body
            @click.stop="selectNetwork(network)"
          >
            <b-row class="d-flex justify-content-end pr-2 pt-1">
              <feather-icon
                v-if="selectedNetwork === network"
                icon="CheckCircleIcon"
                class="text-success"
              />
            </b-row>
            <b-card-header
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 class="mb-0">
                  {{ network.networkType.name }}
                </h5>
                <small class="text-muted"><strong>Org:</strong> {{ network.networkOrgName }}</small>
                <div
                  v-if="network.networkType.identifier === 'tts'"
                >
                  <b-row>
                    <b-col>
                      <small
                        class="text-muted"
                      >
                        <strong>Tenant:</strong> {{ network.networkType.apiEndpoint.split('//')[1].split('.')[0] }}
                      </small>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <small
                        class="text-muted"
                      >
                        <strong>Cluster:</strong> {{ network.networkType.apiEndpoint.split('//')[1].split('.')[1] }}
                      </small>
                    </b-col>
                  </b-row>
                </div>
              </div>
              <b-img
                v-if="network.networkType.logoLink"
                alt="Image"
                :src="network.networkType.logoLink"
                height="64"
                width="64"
              />
            </b-card-header>
            <b-card-body>
              <b-card-text>
                {{ network.networkType.description }}
              </b-card-text>
            </b-card-body>
            <b-card-footer class="text-center">
              <b-button
                :variant="selectedNetwork === network ? 'success' : 'primary'"
                @click.stop="selectNetwork(network)"
              >
                {{ selectedNetwork === network ? "Selected" : "Select" }}
              </b-button>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-form-invalid-feedback :state="!errors[0]">
        {{ errors[0] }}
      </b-form-invalid-feedback>
    </validation-provider>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BCardText,
  BCardFooter,
  BButton,
  BFormInput,
  BFormInvalidFeedback,
  BImg,
} from 'bootstrap-vue'
import { API } from 'aws-amplify'
import { ValidationProvider, extend } from 'vee-validate'
import * as queries from '@/graphql/queries'

extend('network_selected', {
  params: ['selectedNetwork'],
  validate(value, { selectedNetwork }) {
    return !!selectedNetwork
  },
  message: 'Please select a network',
})

require('vue-select-image/dist/vue-select-image.css')

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardText,
    BCardFooter,
    BButton,
    BFormInput,
    BFormInvalidFeedback,
    ValidationProvider,
    BImg,
  },
  props: {
    newDevice: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      networkLoaded: false,
      search: '',
      selectedNetwork: '',
      networks: [],
      networkFields: [
        {
          key: 'networkType.name',
          label: 'Network Name',
          sortable: true,
        },
        {
          key: 'networkOrgName',
          label: 'Network Organization',
          sortable: true,
        },
        {
          key: 'networkType.description',
          label: 'Network Type',
          sortable: true,
        },
      ],
    }
  },
  computed: {
    filteredNetworks() {
      if (!this.search) {
        return this.networks
      }
      return this.networks.filter(
        network => network.networkType.name
          .toLowerCase()
          .includes(this.search.toLowerCase())
          || network.networkOrgName
            .toLowerCase()
            .includes(this.search.toLowerCase())
          || network.networkType.description
            .toLowerCase()
            .includes(this.search.toLowerCase()),
      )
    },
  },
  created() {
    this.listNetworks()
  },
  methods: {
    async listNetworks() {
      const workspaceId = JSON.parse(localStorage.getItem('workspace')).id
      const workspace = await API.graphql({
        query: queries.getWorkspace,
        variables: {
          id: workspaceId,
        },
      })
      const workspaceNetworks = workspace.data.getWorkspace.networks.items
      workspaceNetworks.forEach(async network => {
        await API.graphql({
          query: queries.getNetwork,
          variables: {
            id: network.id,
          },
        }).then(networkInfo => {
          this.networks.push({
            ...networkInfo.data.getNetwork,
          })
        })
      })
    },
    async selectNetwork(network) {
      this.selectedNetwork = network
      this.$emit('network-selected', network)
      const isValid = true
      this.$emit('validation-status', isValid)
    },
    selectedClass(network) {
      return this.selectedNetwork === network ? 'border-success' : ''
    },
    rowClass(item, index) {
      if (this.selectedNetwork === item) {
        return 'bg-success'
      }
      if (index % 2 === 0) {
        return 'bg-light'
      }
      return ''
    },
    networkTooltip() {
      return 'Click to select the network for your device'
    },
    filterNetworks() {},
  },
}
</script>

<style></style>
